import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";

import { ImageLightbox } from "@/components/ImageLightbox";
import { cn } from "@/lib/utils";

import type { ConceptGuide } from "../api";
import { deptLabel } from "../lib";
import { GuideThumb, GuideThumbEmpty } from "./GuideThumb";

/** Steps and sample images a card shows before its "Xem thêm" controls. */
const FIRST_STEPS = 5;
const FIRST_IMAGES = 4;

/**
 * Cạnh dài ảnh mẫu (px). Gấp 3 ô 78px cũ — CEO chốt 27/09/2026 (ảnh nhỏ phóng
 * gấp 3, bấm xem lớn). Khổ 4x6 / 6x4 GIỮ NGUYÊN theo quyết định 29/08/2026:
 * ảnh hướng dẫn là ảnh thẻ 4x6, khung 8x10 sẽ chừa viền hai bên mọi tấm.
 */
const SAMPLE_LONG = 234;

const DEPT_TONE: Record<string, string> = {
  photo: "bg-[var(--color-info-bg)] text-[var(--color-info-text)]",
  makeup: "bg-[var(--color-purple-bg)] text-[var(--color-purple-text)]",
  all: "bg-[var(--color-success-bg)] text-[var(--color-success-text)]",
};

/** A guide's department as a small coloured pill ("📸 Photo"). */
export function DeptPill({ dept }: { dept: string | undefined }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-[7px] py-px text-[10.5px] font-bold",
        DEPT_TONE[dept || "all"] ?? "bg-surface-3 text-muted-foreground",
      )}
    >
      {deptLabel(dept)}
    </span>
  );
}

/**
 * One guide in the shift guide dialog: a header that opens and closes it, and
 * under it the description, the first sample images and the steps.
 */
export function ShiftGuideCard({
  guide,
  expanded,
  onToggle,
}: {
  guide: ConceptGuide;
  expanded: boolean;
  onToggle: () => void;
}) {
  const bodyId = useId();
  const [zoom, setZoom] = useState(-1);

  const images = guide.imageUrls ?? [];
  const steps = guide.steps ?? [];
  const cover = images[0];
  const counts = [
    ...(steps.length > 0 ? [`${steps.length} bước`] : []),
    ...(images.length > 0 ? [`${images.length} ảnh mẫu`] : []),
  ];

  return (
    <div className="rounded-lg border bg-surface shadow-sm">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={expanded ? bodyId : undefined}
        onClick={onToggle}
        className="flex min-h-[60px] w-full items-center gap-2.5 rounded-lg px-[11px] py-2.5 text-left"
      >
        {cover ? <GuideThumb src={cover} long={48} className="rounded-sm" /> : <GuideThumbEmpty long={48} />}
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-bold leading-snug">{guide.title}</span>
          <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11.5px] text-muted-foreground">
            <DeptPill dept={guide.dept} />{" "}
            {counts.length > 0 && <span>{counts.map((c) => `· ${c}`).join(" ")}</span>}
          </span>
        </span>
        <ChevronDown
          size={16}
          aria-hidden
          className={cn("shrink-0 opacity-55 transition-transform", expanded && "rotate-180")}
        />
      </button>

      {expanded && <GuideBody id={bodyId} guide={guide} onZoom={setZoom} />}

      {zoom >= 0 && (
        <ImageLightbox urls={images} index={zoom} onIndex={setZoom} onClose={() => setZoom(-1)} />
      )}
    </div>
  );
}

/** An opened card: the description, the first sample images, then the steps. */
function GuideBody({
  id,
  guide,
  onZoom,
}: {
  id: string;
  guide: ConceptGuide;
  onZoom: (index: number) => void;
}) {
  const [allSteps, setAllSteps] = useState(false);

  const images = guide.imageUrls ?? [];
  const steps = guide.steps ?? [];
  const shownSteps = allSteps ? steps : steps.slice(0, FIRST_STEPS);
  const moreSteps = steps.length - shownSteps.length;
  const moreImages = images.length - FIRST_IMAGES;

  return (
    <div id={id} className="border-t border-dashed px-3 pb-[13px]">
      {guide.description ? (
        <p className="mt-2.5 whitespace-pre-line text-[12.5px] leading-[1.55] text-muted-foreground">
          {guide.description}
        </p>
      ) : null}

      {images.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {images.slice(0, FIRST_IMAGES).map((url, i) => (
            <button
              key={`${url}-${i}`}
              type="button"
              aria-label={`Ảnh mẫu ${i + 1}`}
              onClick={() => onZoom(i)}
              className="shrink-0 overflow-hidden rounded-sm border bg-muted/10"
            >
              <GuideThumb src={url} long={SAMPLE_LONG} />
            </button>
          ))}
          {moreImages > 0 && (
            <button
              type="button"
              aria-label={`Xem thêm ${moreImages} ảnh`}
              onClick={() => onZoom(FIRST_IMAGES)}
              className="flex shrink-0 items-center justify-center rounded-sm bg-surface-3 text-xs font-bold"
              style={{ width: Math.round((SAMPLE_LONG * 2) / 3), height: SAMPLE_LONG }}
            >
              +{moreImages}
            </button>
          )}
        </div>
      )}

      {shownSteps.length > 0 && (
        <ol className="mt-3 space-y-[9px]">
          {shownSteps.map((step, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--color-brand)] text-[11.5px] font-bold text-white">
                {i + 1}
              </span>
              <div className="min-w-0 pt-px">
                <div className="text-[13px] font-bold leading-snug">{step.title}</div>
                {step.body ? (
                  <div className="mt-0.5 whitespace-pre-line text-[12.5px] leading-normal text-muted-foreground">
                    {step.body}
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
      {moreSteps > 0 && (
        <button
          type="button"
          onClick={() => setAllSteps(true)}
          className="mt-1 min-h-11 text-[12.5px] font-bold text-[var(--color-brand)]"
        >
          Xem thêm {moreSteps} bước
        </button>
      )}
    </div>
  );
}
